interface FuncEntry {
    component: any;
    name: string;
    func: Function;
}
export class Action {
    private static funcs: Map<string, FuncEntry[]> = new Map();

    private constructor() {}

    static Add(component: any, key: string, ...func: Function[]) {
        if (!Action.funcs?.has(key)) {
            Action.funcs.set(key, []);
        }

        for (const f of func) {
            Action.funcs.get(key)?.push({ component, name: f.name, func: f });
        }
    }

    static AddAnonymous(component: any, key: string, name: string, func: Function) {
        if (!Action.funcs.has(key)) {
            Action.funcs.set(key, []);
        }

        Action.funcs.get(key)?.push({ component, name, func });
    }

    static Remove(component: any, key: string, ...func: Function[]) {
        for (const f of func) {
            const index = Action.FindIndex(component, f, key);
            if (index !== -1) {
                Action.funcs.get(key)?.splice(index, 1);
            }
        }
    }

    static RemoveByName(component: any, key: string, ...names: string[]) {
        const funcs = Action.funcs.get(key);

        if (!funcs) {
            return;
        }

        for (let i = 0; i < funcs?.length; i++) {

            if (funcs[i]?.component !== component) {
                continue;
            }

            if (names.includes(funcs[i]?.name)) {
                funcs.splice(i, 1);
                i--;
            }
        }
    }

    static Clear(key: string = '') {
        if (key) {
            Action.funcs.delete(key);
        } else {
            Action.funcs.clear();
        }
    }

    static Emit(key: string, ...args: any[]) {
        const funcs = Action.funcs.get(key);

        if (!funcs) {
            return;
        }

        for (const f of funcs) {
            f.func.apply(this, args);
        }
    }

    static EmitExclude(component: any, func: Function, key: string, ...args: any[]) {
        const funcs = Action.funcs.get(key);

        if (!funcs) {
            return;
        }

        const index = Action.FindIndex(component, func, key) || -1;
        for (let i = 0; i < funcs?.length; i++) {
            if (i === index) {
                continue;
            }

            funcs[i].func.apply(this, args);
        }
    }

    private static Find(component: any, func: Function, key: string) {
        const funcs = Action.funcs.get(key);
        return funcs?.find((x) => x.component === component && x.name === func.name);
    }

    private static FindIndex(component: any, func: Function, key: string) {
        const funcs = Action.funcs.get(key);
        return funcs?.findIndex((x) => x.component === component && x.name === func.name);
    }
}

/**
 * Auto-selects the non-sandbox organization when there are exactly 2 organizations
 * and one contains 'sandbox' in its name
 * 
 * @param organizationUsers - Array of organization users
 * @param targetObject - The object containing the property to set (e.g., campaign, formAppointmentSchedulingHour)
 * @param propertyName - The property name to set (e.g., 'organization_id')
 * @returns The filtered organization users array
 * 
 * @example
 * // For campaign form
 * autoSelectNonSandboxOrganization(this.organizationUsers, this.campaign, 'organization_id');
 * 
 * // For appointment scheduling form
 * autoSelectNonSandboxOrganization(this.organizationUsers, this.formAppointmentSchedulingHour, 'organization_id');
 * 
 * // For any other form object
 * autoSelectNonSandboxOrganization(this.organizationUsers, this.myForm, 'organization_id');
 */
export function autoSelectNonSandboxOrganization(
    organizationUsers: any[], 
    targetObject: any, 
    propertyName: string
): any[] {
    if (!organizationUsers || organizationUsers.length === 0) {
        return organizationUsers;
    }

    // Auto-select organization in these cases:
    // 1. If there's only 1 organization total, select it
    // 2. If there are exactly 2 organizations and one is sandbox, select the non-sandbox one
    const nonSandboxOrgs = organizationUsers.filter(x => 
        !x.organization_name?.toLowerCase()?.includes('sandbox')
    );
    
    if (organizationUsers.length === 1) {
        // Only one org, select it
        targetObject[propertyName] = organizationUsers[0].organization_id;
    } else if (organizationUsers.length === 2 && nonSandboxOrgs.length === 1) {
        // Two orgs with one sandbox, select the non-sandbox organization
        targetObject[propertyName] = nonSandboxOrgs[0].organization_id;
    }

    return organizationUsers;
}